import React, { useState, useEffect } from 'react';
import { AlertTriangle, AlertCircle, Info, X, RefreshCw, Check, TrendingDown, TrendingUp, Eye } from 'lucide-react';
import axios from 'axios';

function AnomalyPanel({ onClose }) {
  const [loading, setLoading] = useState(true);
  const [detecting, setDetecting] = useState(false);
  const [anomalies, setAnomalies] = useState([]);
  const [summary, setSummary] = useState({ total: 0, high: 0, medium: 0, low: 0 });
  const [error, setError] = useState('');

  useEffect(() => {
    loadAnomalies();
  }, []);

  const loadAnomalies = async () => {
    try {
      setLoading(true);
      const [anomaliesRes, summaryRes] = await Promise.all([
        axios.get('/anomalies'),
        axios.get('/anomalies/summary')
      ]);
      setAnomalies(anomaliesRes.data);
      setSummary(summaryRes.data);
      setError('');
    } catch (err) {
      setError('Failed to load anomalies');
    } finally {
      setLoading(false);
    }
  };

  const runDetection = async () => {
    try {
      setDetecting(true);
      setError('');
      const response = await axios.post('/anomalies/detect?lookback_months=6');
      setAnomalies(response.data.anomalies);
      setSummary(response.data.summary);
    } catch (err) {
      setError(err.response?.data?.detail || 'Detection failed');
    } finally {
      setDetecting(false);
    }
  };

  const acknowledgeAnomaly = async (anomalyId) => {
    try {
      await axios.put(`/anomalies/${anomalyId}/acknowledge`);
      setAnomalies(prev => prev.filter(a => a.id !== anomalyId));
      setSummary(prev => ({
        ...prev,
        total: prev.total - 1,
        [anomalies.find(a => a.id === anomalyId)?.severity]: prev[anomalies.find(a => a.id === anomalyId)?.severity] - 1
      }));
    } catch (err) {
      setError('Failed to acknowledge anomaly');
    }
  };

  const dismissAnomaly = async (anomalyId) => {
    try {
      await axios.delete(`/anomalies/${anomalyId}`);
      const anomaly = anomalies.find(a => a.id === anomalyId);
      setAnomalies(prev => prev.filter(a => a.id !== anomalyId));
      if (anomaly) {
        setSummary(prev => ({
          ...prev,
          total: prev.total - 1,
          [anomaly.severity]: prev[anomaly.severity] - 1
        }));
      }
    } catch (err) {
      setError('Failed to dismiss anomaly');
    }
  };

  const getSeverityIcon = (severity) => {
    switch (severity) {
      case 'high':
        return <AlertTriangle className="w-5 h-5 text-red-500" />;
      case 'medium':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      default:
        return <Info className="w-5 h-5 text-blue-500" />;
    }
  };

  const getSeverityStyles = (severity) => {
    switch (severity) {
      case 'high':
        return 'border-red-200 bg-red-50';
      case 'medium':
        return 'border-yellow-200 bg-yellow-50';
      default:
        return 'border-blue-200 bg-blue-50';
    }
  };

  const getChangeIcon = (type, deviationPct) => {
    if (type === 'spike' || deviationPct > 0) {
      return <TrendingUp className="w-4 h-4 text-green-500" />;
    } else if (type === 'drop' || deviationPct < 0) {
      return <TrendingDown className="w-4 h-4 text-red-500" />;
    }
    return null;
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="anomaly-panel">
        <div className="bg-white rounded-2xl p-8 max-w-3xl w-full mx-4">
          <div className="flex justify-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" data-testid="anomaly-panel">
      <div className="bg-white rounded-2xl max-w-3xl w-full mx-4 max-h-[90vh] overflow-hidden shadow-2xl">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-orange-500 p-6 text-white">
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <AlertTriangle className="w-8 h-8" />
              <div>
                <h2 className="text-2xl font-bold">Anomaly Detection</h2>
                <p className="text-white/80">Monitor unusual changes in your Trust Score</p>
              </div>
            </div>
            <button 
              onClick={onClose} 
              className="text-white/80 hover:text-white transition"
              data-testid="close-anomaly-panel"
            >
              <X size={24} />
            </button>
          </div>

          {/* Summary Stats */}
          <div className="flex gap-4 mt-6">
            <div className="bg-white/20 rounded-lg px-4 py-2">
              <p className="text-white/70 text-sm">Total</p>
              <p className="text-2xl font-bold">{summary.total}</p>
            </div>
            <div className="bg-red-600/50 rounded-lg px-4 py-2">
              <p className="text-white/70 text-sm">High</p>
              <p className="text-2xl font-bold">{summary.high}</p>
            </div>
            <div className="bg-yellow-500/50 rounded-lg px-4 py-2">
              <p className="text-white/70 text-sm">Medium</p>
              <p className="text-2xl font-bold">{summary.medium}</p>
            </div>
            <div className="bg-blue-500/50 rounded-lg px-4 py-2">
              <p className="text-white/70 text-sm">Low</p>
              <p className="text-2xl font-bold">{summary.low}</p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 220px)' }}>
          {/* Run Detection Button */}
          <button
            onClick={runDetection}
            disabled={detecting}
            className="w-full mb-6 py-3 px-6 bg-gradient-to-r from-red-500 to-orange-500 text-white font-semibold rounded-xl hover:from-red-600 hover:to-orange-600 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="run-detection"
          >
            {detecting ? (
              <>
                <RefreshCw className="w-5 h-5 animate-spin" />
                Analyzing Data...
              </>
            ) : (
              <>
                <Eye className="w-5 h-5" />
                Run Anomaly Detection
              </>
            )}
          </button>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
              {error}
            </div>
          )}

          {/* Anomalies List */}
          {anomalies.length === 0 ? (
            <div className="text-center py-12">
              <Check className="w-16 h-16 text-green-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No Anomalies Detected</h3>
              <p className="text-gray-600">All metrics and scores are within normal ranges.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {anomalies.map((anomaly) => (
                <div
                  key={anomaly.id}
                  className={`border rounded-xl p-4 ${getSeverityStyles(anomaly.severity)}`}
                  data-testid={`anomaly-${anomaly.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-3">
                      {getSeverityIcon(anomaly.severity)}
                      <div>
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900">{anomaly.entity_name}</h4>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                            anomaly.severity === 'high' ? 'bg-red-200 text-red-800' :
                            anomaly.severity === 'medium' ? 'bg-yellow-200 text-yellow-800' :
                            'bg-blue-200 text-blue-800'
                          }`}>
                            {anomaly.severity.toUpperCase()}
                          </span>
                        </div>
                        <p className="text-gray-700 mt-1">{anomaly.message}</p>
                        
                        {/* Value Details */}
                        {anomaly.current_value !== null && (
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <div className="flex items-center gap-1">
                              {getChangeIcon(anomaly.type, anomaly.deviation_pct)}
                              <span className="text-gray-500">
                                {anomaly.expected_value?.toFixed(1)} → {anomaly.current_value.toFixed(1)}
                              </span>
                            </div>
                            {anomaly.deviation_pct && (
                              <span className={`font-medium ${
                                anomaly.deviation_pct > 0 ? 'text-green-600' : 'text-red-600'
                              }`}>
                                {anomaly.deviation_pct > 0 ? '+' : ''}{anomaly.deviation_pct.toFixed(1)}%
                              </span>
                            )}
                            {anomaly.z_score && (
                              <span className="text-gray-500">
                                Z-score: {anomaly.z_score.toFixed(2)}
                              </span>
                            )}
                          </div>
                        )}

                        {/* Detected Time */}
                        <p className="text-xs text-gray-400 mt-2">
                          Detected: {new Date(anomaly.detected_at).toLocaleString()}
                        </p>
                      </div>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <button
                        onClick={() => acknowledgeAnomaly(anomaly.id)}
                        className="p-2 hover:bg-white/50 rounded-lg transition text-gray-500 hover:text-green-600"
                        title="Mark as reviewed"
                      >
                        <Check className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => dismissAnomaly(anomaly.id)}
                        className="p-2 hover:bg-white/50 rounded-lg transition text-gray-500 hover:text-red-600"
                        title="Dismiss"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Small badge component to show on dashboard
export function AnomalyBadge({ onClick, refreshTrigger }) {
  const [summary, setSummary] = useState({ total: 0, high: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadSummary();
  }, [refreshTrigger]);

  const loadSummary = async () => {
    try {
      const response = await axios.get('/anomalies/summary');
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to load anomaly summary');
    } finally {
      setLoading(false);
    }
  };

  if (loading || summary.total === 0) {
    return null;
  }

  return (
    <button
      onClick={onClick}
      className="flex items-center gap-2 px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 transition animate-pulse"
      data-testid="anomaly-badge"
    >
      <AlertTriangle className="w-4 h-4" />
      <span>{summary.total} Alert{summary.total !== 1 ? 's' : ''}</span>
      {summary.high > 0 && (
        <span className="bg-red-700 px-2 py-0.5 rounded-full text-xs">
          {summary.high} High
        </span>
      )}
    </button>
  );
}

export default AnomalyPanel;
